import React, { useState, useEffect } from "react";
import { Box, Divider, makeStyles, Typography } from "@material-ui/core";
import MDEditor from "@uiw/react-md-editor";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    position: "relative",
    height: "400px",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    overflow: "hidden",
    resize: "vertical",
    minHeight: "400px",
  },
  editorPane: {
    flex: 1,
    overflow: "auto",
    borderRight: "1px solid #d1d5db",
    "& .w-md-editor": {
      height: "100% !important",
      boxShadow: "none",
      border: "none",
    },
    "& .w-md-editor-toolbar": {
      borderBottom: "1px solid #d1d5db",
    },
    "& .w-md-editor-content": {
      height: "calc(100% - 29px) !important",
    },
  },
  previewPane: {
    flex: 1,
    overflow: "auto",
    backgroundColor: "#fff",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontSize: "16px",
    lineHeight: "1.6",
    "& h1": {
      fontSize: "2em",
      fontWeight: 600,
      marginTop: "24px",
      marginBottom: "16px",
      borderBottom: "1px solid #d1d5db",
      paddingBottom: "0.3em",
    },
    "& h2": {
      fontSize: "1.5em",
      fontWeight: 600,
      marginTop: "24px",
      marginBottom: "16px",
      borderBottom: "1px solid #d1d5db",
      paddingBottom: "0.3em",
    },
    "& h3": {
      fontSize: "1.25em",
      fontWeight: 600,
      marginTop: "24px",
      marginBottom: "16px",
    },
    "& h4": {
      fontSize: "1em",
      fontWeight: 600,
      marginTop: "24px",
      marginBottom: "16px",
    },
    "& p": {
      marginTop: 0,
      marginBottom: "16px",
    },
    "& code": {
      backgroundColor: "rgba(175, 184, 193, 0.2)",
      padding: "0.2em 0.4em",
      borderRadius: "3px",
      fontSize: "90%",
      fontFamily: "monospace",
    },
    "& pre": {
      backgroundColor: "#f6f8fa",
      padding: "16px",
      overflow: "auto",
      fontSize: "90%",
      lineHeight: "1.45",
      borderRadius: "3px",
      marginBottom: "16px",
    },
    "& pre code": {
      backgroundColor: "transparent",
      padding: 0,
      fontSize: "100%",
    },
    "& ul, & ol": {
      paddingLeft: "2em",
      marginBottom: "16px",
    },
    "& li": {
      marginTop: "0.25em",
    },
    "& blockquote": {
      padding: "0 1em",
      color: "#57606a",
      borderLeft: "0.25em solid #d0d7de",
      marginBottom: "16px",
    },
    "& table": {
      borderCollapse: "collapse",
      borderSpacing: 0,
      marginBottom: "16px",
      width: "100%",
    },
    "& table th": {
      fontWeight: 600,
      padding: "6px 13px",
      border: "1px solid #d0d7de",
    },
    "& table td": {
      padding: "6px 13px",
      border: "1px solid #d0d7de",
    },
    "& hr": {
      height: "0.25em",
      padding: 0,
      margin: "24px 0",
      backgroundColor: "#d0d7de",
      border: 0,
    },
    "& img": {
      maxWidth: "100%",
      boxSizing: "content-box",
    },
    "& strong, & b": {
      fontWeight: 700,
      color: "#1a1a1a",
    },
    "& .katex": {
      fontSize: "1.15em",
    },
  },
}));

export default function MarkdownEditorWithPreview({
  value,
  onChange = () => {},
  disabled = false,
  error = null,
  height = "400px",
  placeholder = "Write your markdown here...",
}) {
  const classes = useStyles();
  const [editorValue, setEditorValue] = useState(value || "");

  useEffect(() => {
    if (value !== undefined) {
      setEditorValue(value);
    }
  }, [value]);

  const handleEditorChange = (newValue) => {
    setEditorValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <Box>
      <Box
        className={classes.container}
        style={{
          height,
          borderColor: error ? "#f44336" : "#d1d5db",
          borderWidth: error ? "2px" : "1px",
        }}
      >
        {/* Editor Pane */}
        <div className={classes.editorPane} data-color-mode="light">
          <MDEditor
            value={editorValue}
            onChange={handleEditorChange}
            preview="edit"
            hideToolbar={false}
            height="100%"
            disabled={disabled}
            visibleDragbar={false}
            textareaProps={{
              placeholder: placeholder,
            }}
          />
        </div>

        {/* Preview Pane */}
        <div className={classes.previewPane}>
          <div
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <Typography
              variant="body2"
              style={{
                margin: 0,
                marginLeft: "8px",
                paddingTop: "8px",
                marginBottom: "4px",
                fontWeight: "600",
                fontFamily: "Times New Roman, Times, serif",
              }}
              gutterBottom
            >
              Preview
            </Typography>
            <Divider
              style={{
                margin: 0,
                padding: 0,
                height: "0.05em",
              }}
            />
          </div>
          <div
            style={{
              margin: "16px",
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeKatex]}
            >
              {editorValue || ""}
            </ReactMarkdown>
          </div>
        </div>
      </Box>
      {error && (
        <Typography
          variant="body2"
          style={{
            color: "#f44336",
            marginTop: "8px",
            fontSize: "14px",
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}
